import React, { useContext } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Tabs,
  Tab,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import MapIcon from "@mui/icons-material/Map";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import UserMenu from "../UserMenu/UserMenu";
import LanguageSelector from "../../../Advanced/LanguageSelector/LanguageSelector";
import { AlertsBell } from "../../../../modules/shared/alerts";
import { APPBAR_ITEMS } from "../../../../config/navigationConfig";
import { useViewAsRole } from "../../../../context/ViewAsRoleContext/ViewAsRoleContext";
import { AuthContext } from "../../../../context/AuthContext/AuthContext";
import { canUserAccessRoute } from "../../../../app/AccessDenied/roleRoutePermissions";

const ICON_MAP = {
  HomeIcon: HomeIcon,
  MapIcon: MapIcon,
  SupportAgentIcon: SupportAgentIcon,
};

const AppBarComponent = ({ handleOpenNavMenu, WidthDrawDate }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { viewAsRole, viewAsRoleLabel, resetRole, hasViewAsPermission } = useViewAsRole();
  const { user } = useContext(AuthContext);

  const currentPath = location.pathname.toLowerCase();

  // Filtrar items por permisos.
  // Cuando admin previsualiza otro rol con viewAsRole, respetamos los
  // permisos hardcodeados del rol simulado (es solo una vista previa).
  // Para el usuario real, gateamos por ruta usando user.modules — la misma
  // fuente que RoleGuard y NavigationSections.
  const visibleItems = APPBAR_ITEMS.filter((item) => {
    const viewAsResult = hasViewAsPermission(item.permissions);
    if (viewAsResult !== null) return viewAsResult;
    return canUserAccessRoute(user, item.path);
  });

  const tabValue = visibleItems.some(
    (item) => item.path.toLowerCase() === currentPath
  )
    ? currentPath
    : false;

  return (
    <AppBar
      position="fixed"
      sx={{
        width: { md: `calc(100% - ${WidthDrawDate}px)` },
        ml: { md: `${WidthDrawDate}px` },
        transition: "width 0.3s ease, margin 0.3s ease",
      }}
    >
      <Toolbar>
        {/* Hamburguesa para mobile */}
        <IconButton
          color="inherit"
          aria-label="open menu"
          edge="start"
          onClick={handleOpenNavMenu}
          sx={{ mr: 2, display: { md: "none" } }}
        >
          <MenuIcon />
        </IconButton>

        {/* Tabs de navegacion principal */}
        <Tabs
          value={tabValue}
          textColor="inherit"
          indicatorColor="secondary"
          sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}
        >
          {visibleItems.map((item) => {
            const IconComponent = ICON_MAP[item.icon];
            return (
              <Tab
                key={item.id}
                label={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {IconComponent && <IconComponent sx={{ mr: 1 }} />}
                    <Typography>{t(item.labelKey, item.labelFallback)}</Typography>
                  </Box>
                }
                value={item.path.toLowerCase()}
                component={Link}
                to={item.path}
                sx={{
                  textTransform: "none",
                  minWidth: 100,
                  color: "inherit",
                  opacity: 0.7,
                  "&.Mui-selected": {
                    color: "white",
                    opacity: 1,
                    fontWeight: "bold",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    borderRadius: 1,
                  },
                }}
              />
            );
          })}
        </Tabs>

        {/* Mobile: mostrar titulo de pagina actual */}
        <Typography
          variant="subtitle1"
          sx={{
            flexGrow: 1,
            display: { xs: "block", md: "none" },
            fontWeight: 600,
          }}
        >
          MCM
        </Typography>

        {/* View As Role banner */}
        {viewAsRole && (
          <Chip
            label={`${t("nav.viewingAs", "Viewing as")}: ${viewAsRoleLabel}`}
            size="small"
            color="info"
            onDelete={resetRole}
            sx={{ mr: 1 }}
          />
        )}

        {/* Controles de la derecha */}
        <Box sx={{ flexGrow: 0, display: "flex", alignItems: "center", gap: 1.5 }}>
          <AlertsBell />
          <LanguageSelector />
          <UserMenu />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default AppBarComponent;
